import { Route, Routes } from 'react-router-dom';

import Configure from './pages/Configure';
import SignIn from './pages/SignIn';
import VerifySignIn from './pages/VerifySignIn';
import Orders from './pages/Orders';
import OrderStatus from './pages/OrderStatus';
import ConfirmPayoutChange from './pages/ConfirmPayoutChange';
import Account from './pages/Account';
import PaymentReturn from './pages/PaymentReturn';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import { SessionProvider } from './hooks/useSession';

/**
 * Application shell.
 *
 * `/sign-in/verify` matches the URL the backend builds into magic-link emails, and
 * `/orders/:orderNumber` matches the tracking link in the order receipt. Both are
 * composed server-side from PUBLIC_WEB_URL, so these paths and that configuration have
 * to agree.
 *
 * `/terms` and `/privacy` are linked from the footer of every page and are reachable
 * directly, which is the point of giving a legal document a URL rather than a modal.
 *
 * The session provider wraps everything: the header needs to know whether anyone is
 * signed in, and so do the protected pages -- and the footer's support form, which
 * prefills a signed-in customer's address from it.
 */
export default function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/" element={<Configure />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-in/verify" element={<VerifySignIn />} />
        <Route path="/orders" element={<Orders />} />
        {/* My Account. One section for now, because the payout wallet is the only piece
            of account state a passwordless customer actually owns. */}
        <Route path="/account" element={<Account />} />
        <Route path="/orders/:orderNumber" element={<OrderStatus />} />
        {/* Where SaturnShift's hosted checkout returns the customer. The path is composed
            server-side in services/saturnshift/checkoutConfig.js from PUBLIC_WEB_URL, so
            this route and that builder have to agree exactly. Unlike the order page it is
            NOT behind a session: a customer coming back from paying has not necessarily
            signed in. Arriving here proves nothing — see pages/PaymentReturn.tsx. */}
        <Route path="/orders/:orderNumber/return" element={<PaymentReturn />} />
        {/* Where an emailed payout-change confirmation link lands. Deliberately NOT
            session-gated: the customer may open it on the device their email is on. */}
        <Route path="/payout-address/confirm" element={<ConfirmPayoutChange />} />
        {/* The legal documents. Own URLs so they can be linked to, bookmarked and
            cited -- see components/LegalDocument.tsx. */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Configure />} />
      </Routes>
    </SessionProvider>
  );
}
